/**
 * Detects if the user came from Meta campaigns
 * by checking for meta=true parameter in URL
 */

/**
 * Checks if user came from Meta campaign
 * @returns {boolean} true if meta=true parameter exists in URL
 */
export function isFromMeta() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('meta') === 'true';
}

/**
 * Stores Meta source info in localStorage permanently
 * This persists across browser sessions - once a Meta user, always a Meta user
 */
export function trackMetaSource() {
  if (isFromMeta()) {
    // Only set these if not already set (preserve first visit data)
    if (!localStorage.getItem('isFromMeta')) {
      localStorage.setItem('isFromMeta', 'true');
      localStorage.setItem('metaLandingTime', new Date().toISOString());
      localStorage.setItem('metaLandingPage', window.location.pathname);
      console.log('✓ Meta campaign traffic detected - user permanently marked');
    } else {
      console.log('✓ Returning Meta user detected');
    }
    return true;
  }
  return false;
}

/**
 * Checks if the user ever came from Meta (even in past sessions)
 * Checks both current URL and localStorage
 * @returns {boolean}
 */
export function isMetaSession() {
  // Check if currently has meta=true in URL
  if (isFromMeta()) {
    return true;
  }

  // Check if user was ever marked as Meta user (stored in localStorage)
  return localStorage.getItem('isFromMeta') === 'true';
}

/**
 * Gets stored Meta source information
 * @returns {object|null} Meta source data or null
 */
export function getMetaSourceData() {
  if (!isMetaSession()) {
    return null;
  }

  return {
    landingTime: localStorage.getItem('metaLandingTime'),
    landingPage: localStorage.getItem('metaLandingPage'),
    isFromMeta: true
  };
}

/**
 * Sets Meta flag locally without checking URL
 * Used by fingerprint rehydration to mark user as Meta
 */
export function setMetaLocally() {
  if (localStorage.getItem('isFromMeta') !== 'true') {
    localStorage.setItem('isFromMeta', 'true');
    if (!localStorage.getItem('metaLandingTime')) {
      localStorage.setItem('metaLandingTime', new Date().toISOString());
      localStorage.setItem('metaLandingPage', window.location.pathname);
    }
  }
}

/**
 * Checks for weak Meta hints (referrer or URL params)
 * Used to allow IP-based fallback only when there are signs of Meta traffic
 * @returns {boolean}
 */
export function hasWeakMetaHint() {
  try {
    const p = new URLSearchParams(window.location.search);
    const ref = document.referrer || "";
    return (
      ref.includes('facebook.com') || ref.includes('instagram.com') ||
      p.has('fbclid') || p.has('igshid')
    );
  } catch {
    return false;
  }
}

/**
 * 24h-Backoff per mechanism (e.g. 'fp' or 'ip')
 * Prevents excessive expensive calls to functions/firestore
 * @param {string} kind - mechanism type ('fp' or 'ip')
 * @returns {boolean} true if attempt is allowed
 */
export function shouldAttempt(kind) {
  try {
    const key = `metaRehydrate_${kind}_last`;
    const last = parseInt(localStorage.getItem(key) || '0', 10);
    const now = Date.now();
    if (now - last < 24*60*60*1000) return false; // < 24h
    localStorage.setItem(key, String(now));
    return true;
  } catch {
    // If localStorage blocked → allow one attempt
    return true;
  }
}
